import { type FC, useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useTonConnectUI, useTonWallet } from '@tonconnect/ui-react';

import {
  useLazyTonGeneratePayloadQuery,
  useTonLoginMutation,
} from '@/api/queries/auth';
import { AppRoute } from '@/common/app/routes';
import { Loader } from '@/components/ui/Loader/Loader';
import { showToast } from '@/components/ui/Notification/SwipeableToastNotification';

const TonWalletLoginButton: FC = () => {
  const { t } = useTranslation();
  const [tonConnectUI] = useTonConnectUI();
  const wallet = useTonWallet();
  const navigate = useNavigate();
  const [generatePayload] = useLazyTonGeneratePayloadQuery();
  const [tonLogin] = useTonLoginMutation();
  const [isLoading, setIsLoading] = useState(false);
  const awaitingProofRef = useRef(false);

  const handleLogin = useCallback(async () => {
    setIsLoading(true);

    try {
      // Generate payload from backend
      const { data: payloadData } = await generatePayload();

      if (!payloadData) {
        showToast(t('Failed to generate payload. Please try again.'));
        setIsLoading(false);

        return;
      }

      // Set tonProof BEFORE opening modal / reconnecting
      tonConnectUI.setConnectRequestParameters({
        state: 'ready',
        value: { tonProof: payloadData.payload },
      });

      awaitingProofRef.current = true;

      if (wallet) {
        // Wallet already connected — disconnect first, modal will open from onStatusChange
        await tonConnectUI.disconnect();
      } else {
        await tonConnectUI.openModal();
      }
    } catch {
      showToast(t('Something went wrong, please try again'));
    } finally {
      setIsLoading(false);
    }
  }, [wallet, tonConnectUI, generatePayload, t]);

  useEffect(() => {
    const unsubscribe = tonConnectUI.onStatusChange(async (walletInfo) => {
      if (walletInfo) {
        // Wallet connected
        if (!awaitingProofRef.current) return;

        const proof = walletInfo.connectItems?.tonProof;

        if (!proof || proof.name !== 'ton_proof') {
          awaitingProofRef.current = false;

          return;
        }

        if ('error' in proof) {
          showToast(t('Wallet proof verification failed'));
          awaitingProofRef.current = false;

          return;
        }

        try {
          await tonLogin({
            address: walletInfo.account.address,
            network: walletInfo.account.chain,
            publicKey: walletInfo.account.publicKey,
            walletStateInit: walletInfo.account.walletStateInit,
            proof: {
              timestamp: proof.proof.timestamp,
              domain: proof.proof.domain,
              signature: proof.proof.signature,
              payload: proof.proof.payload,
            },
          }).unwrap();

          // Token is set by userReducer extraReducers (authApi.endpoints.tonLogin.matchFulfilled)
          navigate(AppRoute.MAIN);
        } catch {
          showToast(t('Something went wrong, please try again'));
          await tonConnectUI.disconnect();
        } finally {
          awaitingProofRef.current = false;
          tonConnectUI.setConnectRequestParameters({ state: 'loading' });
        }
      } else {
        // Wallet disconnected
        if (awaitingProofRef.current) {
          // Disconnect completed — now open modal for reconnect with tonProof
          await tonConnectUI.openModal();
        }
      }
    });

    return (): void => {
      unsubscribe();
    };
  }, [tonConnectUI, tonLogin, navigate, t]);

  return (
    <button
      onClick={handleLogin}
      disabled={isLoading}
      className="w-full h-12 flex items-center justify-center gap-2 rounded-lg bg-[#0098EA] active:scale-95 transition-transform duration-150 ease-in-out"
    >
      {isLoading ? (
        <Loader />
      ) : (
        <>
          <img src="/img/icons/ton.svg" alt="TON" className="w-6 h-6" />
          <span className="text-white font-bold text-base">
            {t('Continue with TON Wallet')}
          </span>
        </>
      )}
    </button>
  );
};

export { TonWalletLoginButton };
